"use client";

import * as React from "react";

import { cn } from "@/lib/utils/utils";

interface ShineBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Width of the border in pixels
   * @default 1
   */
  borderWidth?: number;
  /**
   * Duration of the animation in seconds
   * @default 14
   */
  duration?: number;
  /**
   * Color of the border, can be a single color or an array of colors
   * @default "#000000"
   */
  shineColor?: string | string[];
  /**
   * Opacity of the gradient overlay
   * @default 0.5
   */
  gradientOpacity?: number;
  /** Additional class names for the component
   */
  gradientDegree?: number;
}

/**
 * Shine Border
 *
 * An animated background border effect component with configurable properties.
 */
export function GradientBorder({
  borderWidth = 1,
  duration = 14,
  shineColor = "#000000",
  gradientOpacity = 0.5,
  gradientDegree = 135,

  className,
  style,
  ...props
}: ShineBorderProps) {
  return (
    <div
      style={
        {
          "--border-width": `${borderWidth}px`,
          // Diagonal gradient from top-left to bottom-right
          backgroundImage: `linear-gradient(
            ${gradientDegree}deg,
            rgba(255, 255, 255, ${gradientOpacity}) 0%,
            rgba(255, 255, 255, 0.05) 30%,
            rgba(255, 255, 255, 0.05) 70%,
            rgba(255, 255, 255, ${gradientOpacity}) 100%
          )`,
          mask: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
          WebkitMask: `linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)`,
          WebkitMaskComposite: "xor",
          maskComposite: "exclude",
          padding: "var(--border-width)",
          ...style,
        } as React.CSSProperties
      }
      className={cn(
        "pointer-events-none absolute inset-0 size-full rounded-[inherit]",
        className,
      )}
      {...props}
    />
  );
}
